import React, { FC, useMemo, useState } from 'react';
import Box from '@material-ui/core/Box';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { findIndex } from 'lodash';

import CustomSelect from '../../../common/components/CustomSelect';
import CustomTextField from '../../../common/components/CustomTextField';
import { InputType } from '../../../common/types';
import { CreateUserInput } from '../../../types/graphql-global-types';
import {
  APAS,
  CIVILITIES,
  Civility,
  EnseigneTag,
  Role,
  ROLES,
} from '../../../common/constants';
import { useMinimalEnseignes } from '../../../hooks/queries/useEnseign';
import CustomAutoComplete from '../../../common/components/CustomAutoComplete';
import { useAutoCompleteEntities } from '../../../hooks/queries/useEntity';
import useDebounce from '../../../hooks/useDebounce';
import { useCreateUser } from '../../../hooks/mutations/useCreateUser';
import { useGetMandataries } from '../../../hooks/queries/useGetMandataries';
import { formatName } from '../../../utils/common';
import { insert } from '../../../common/logics';
import CustomPhoneInput from '../../../common/components/CustomPhoneInput';
import { displaySnackBar } from '../../../common/displaySnackbar';
import { isEmailValid as validateEmail } from '../../../common/validator';
import { isValidPhoneNumber } from 'react-phone-number-input';
import ConfirmationButtons from '../../ConfirmationButtons/ConfirmationButtons';
import CheckBox from '@material-ui/core/Checkbox';
import { FormControlLabel } from '@material-ui/core';

interface IUser extends Omit<CreateUserInput, 'roles'> {
  role: string;
  isMobile?: boolean;
}

type UserFormType = InputType & { key: keyof IUser };

type SearchStateType = {
  enseign: string;
  entity: string;
  mandatary: string;
};

const initialSearchState: SearchStateType = {
  enseign: '',
  entity: '',
  mandatary: '',
};
const initialUserState: IUser = {
  civility: '',
  firstName: '',
  lastName: '',
  role: '',
  agencyId: 0,
  agencyAddress: '',
  brandOrStudyName: '',
  email: '',
  enseignId: -1,
  mandataryId: -1,
  phone: '',
};

interface UserFormProps {
  onCancel: () => void;
  onCreate: () => void;
  setIsLoading: (isLoading: boolean) => void;
}

const UserForm: FC<UserFormProps> = (props) => {
  const classes = useStyles();

  const [userInputs, setUserInputs] = useState<IUser>(initialUserState);
  const [autoCompleteSearch, setAutoCompleteSearch] =
    useState<SearchStateType>(initialSearchState);
  const [isFinishFillInputs, setIsFinishFillInputs] = useState<boolean>(false);
  const [isEmailValid, setIsEmailValid] = useState<boolean>(true);

  const debouncedEnseignSearch = useDebounce(autoCompleteSearch?.enseign, 300);
  const debouncedEntitySearch = useDebounce(autoCompleteSearch?.entity, 300);

  const isCurrentSelectedRoleMandatary =
    (userInputs.role as Role) === Role.MANDATARY;
  const isCurrentSelectedRoleAdmin = (userInputs.role as Role) === Role.ADMIN;

  const {
    data: autoCompleteEnseignData,
    loading: loadingAutoCompleteEnseigne,
  } = useMinimalEnseignes(
    debouncedEnseignSearch.length > 2 ? debouncedEnseignSearch : '',
    isCurrentSelectedRoleMandatary ? EnseigneTag.CONNECTCREDIT : undefined,
  );

  const { data: autoCompleteEntityData, loading: loadingAutoCompleteEntity } =
    useAutoCompleteEntities(
      debouncedEntitySearch.length > 2 ? debouncedEntitySearch : '',
      userInputs?.enseignId ?? -1,
    );
  const {
    data: autoCompleteMandataryData,
    loading: loadingAutoCompleteMandataries,
  } = useGetMandataries();

  const [createUser, { loading: loadingCreateUser, client }] = useCreateUser();

  const autoCompleteEnseignOptions: {
    label: string;
    value: any;
  }[] = useMemo(() => {
    return (
      autoCompleteEnseignData?.allEnseignes?.enseignes?.map((enseign) => ({
        label: enseign?.name ?? '',
        value: enseign?.id,
      })) || []
    );
  }, [autoCompleteEnseignData]);

  const autoCompleteEntityOptions: {
    label: string;
    value: any;
  }[] = useMemo(() => {
    return (
      autoCompleteEntityData?.allEntities?.entities?.map((entity) => ({
        label: entity?.name || '',
        value: entity?.id,
      })) || []
    );
  }, [autoCompleteEntityData]);

  const autoCompleteMandataryOptions: {
    label: string;
    value: any;
  }[] = useMemo(() => {
    return (
      autoCompleteMandataryData?.mandataries?.map((mandatary) => ({
        label: formatName({
          firstName: mandatary?.firstName,
          lastName: mandatary?.lastName,
        }),
        value: mandatary?.id,
      })) || []
    );
  }, [autoCompleteMandataryData]);

  let inputs: UserFormType[] = [
    {
      type: 'select',
      label: 'Civilité',
      key: 'civility',
      value: userInputs.civility,
      options: Object.values(Civility).map((_civility) => ({
        label: CIVILITIES[_civility],
        value: _civility,
      })),
      required: true,
      helperText: 'Veuillez choisir la civilité',
    },
    {
      type: 'text',
      label: 'Nom',
      key: 'lastName',
      value: userInputs.lastName || '',
      required: true,
      helperText: 'Veuillez remplir le nom',
    },
    {
      type: 'text',
      label: 'Prénom',
      key: 'firstName',
      value: userInputs.firstName,
      required: true,
      helperText: 'Veuillez remplir le prénom',
    },
    {
      type: 'select',
      label: 'Rôle',
      key: 'role',
      value: userInputs.role,
      options: Object.keys(ROLES).map((_role) => ({
        label: ROLES[_role],
        value: _role,
      })),
      required: true,
      helperText: 'Veuillez choisir un rôle',
    },
    {
      type: 'autoComplete',
      label: "Nom de l'enseigne",
      key: 'enseignId',
      value: userInputs.enseignId,
      options: autoCompleteEnseignOptions,
      required: true,
      helperText: 'Veuillez choisir une enseigne',
    },
    {
      type: 'autoComplete',
      label: "Nom de l'agence",
      key: 'agencyId',
      value: userInputs.agencyId,
      options: autoCompleteEntityOptions,
      required: true,
      helperText: 'Veuillez choisir une agence',
    },
    {
      type: 'email',
      label: 'Email',
      key: 'email',
      value: userInputs.email || '',
      required: true,
      helperText: 'Veuillez entrer une adresse email valide',
    },
    {
      type: 'tel',
      label: 'Téléphone',
      key: 'phone',
      value: userInputs?.phone || '',
      required: true,
      helperText: 'Veuillez entrer un numéro de téléphone',
    },
    // {
    //   type: 'checkbox',
    //   label: 'Peut accéder au mobile',
    //   key: 'isMobile',
    //   value: !!userInputs?.isMobile,
    //   required: false,
    //   helperText: "Veuillez cocher la case pour donner l'accès au mobile",
    // },
  ];

  if (APAS.includes(userInputs.role as Role))
    inputs = insert(inputs, 3, {
      type: 'autoComplete',
      label: 'Expert Connectcrédit',
      key: 'mandataryId',
      value: userInputs?.mandataryId,
      options: autoCompleteMandataryOptions,
      required: true,
      helperText: 'Veuillez choisire un mandataire',
    });

  if ([Role.INDIVIDUAL].includes(userInputs.role as Role))
    inputs = inputs?.filter(
      (input) => !['enseignId', 'agencyId'].includes(input?.key),
    );

  if (isCurrentSelectedRoleMandatary || isCurrentSelectedRoleAdmin) {
    const indexOfEnseigne = findIndex(
      inputs,
      (input) => input.key === 'enseignId',
    );
    inputs.splice(indexOfEnseigne, 1);
  }

  if (isCurrentSelectedRoleAdmin) {
    const indexOfAgency = findIndex(
      inputs,
      (input) => input.key === 'agencyId',
    );
    inputs.splice(indexOfAgency, 1);
  }

  React.useEffect(() => {
    if (
      isCurrentSelectedRoleMandatary &&
      autoCompleteEnseignData?.allEnseignes?.enseignes
    ) {
      setAutoCompleteSearch((prevState) => ({ ...prevState, enseign: '' }));
      setUserInputs((prevState) => ({
        ...prevState,
        enseignId: autoCompleteEnseignData?.allEnseignes?.enseignes
          ? autoCompleteEnseignData?.allEnseignes?.enseignes[0]?.id
          : 0,
      }));
    }
    if (!isCurrentSelectedRoleMandatary) {
      setUserInputs((prevState) => ({
        ...prevState,
        enseignId: 0,
      }));
    }
  }, [isCurrentSelectedRoleMandatary, autoCompleteEnseignData]);

  const handleChange = (key: string, value: string) => {
    setUserInputs((prevState) => ({ ...prevState, [key]: value }));
  };

  const hanleAutoCompleteInputChange = (key: string, value: string) => {
    setAutoCompleteSearch((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const handleCreate = () => {
    if (isRequiredInputsValid()) {
      props?.setIsLoading(true);
      createUser({
        variables: {
          input: {
            email: userInputs?.email,
            phone: userInputs?.phone,
            civility: userInputs?.civility,
            firstName: userInputs?.firstName,
            lastName: userInputs?.lastName,
            withEmail: false,
            roles: [userInputs?.role],
            enseignId: userInputs?.enseignId,
            agencyId: userInputs?.agencyId,
            brandOrStudyName: userInputs?.enseignId
              ? ''
              : autoCompleteSearch?.enseign,
            agencyAddress: userInputs?.agencyId
              ? ''
              : autoCompleteSearch?.entity,
            mandataryId: userInputs?.mandataryId,
            isMobile: !!userInputs?.isMobile,
          },
        },
      }).then((result) => {
        props?.setIsLoading(false);
        if (result && result?.data && result?.data?.createUser) {
          setUserInputs(initialUserState);
          setAutoCompleteSearch(initialSearchState);
          setIsFinishFillInputs(false);
          props?.onCreate();
          displaySnackBar(client, {
            type: 'SUCCESS',
            message: 'Ajouté avec succès',
            isOpen: true,
          });
        }
      });
    }
  };

  const isRequiredInputsValid = () => {
    if (
      autoCompleteSearch.enseign &&
      autoCompleteSearch.entity &&
      !userInputs?.agencyId &&
      !userInputs?.enseignId
    )
      return (
        !inputs.filter(
          (input) =>
            input?.key !== 'enseignId' &&
            input?.key !== 'agencyId' &&
            input?.required &&
            !input?.value,
        )?.length &&
        autoCompleteSearch?.enseign &&
        autoCompleteSearch?.entity
      );

    if (autoCompleteSearch.enseign && !userInputs?.enseignId)
      return (
        !inputs.filter(
          (input) =>
            input?.key !== 'enseignId' && input?.required && !input?.value,
        )?.length && autoCompleteSearch?.enseign
      );

    if (autoCompleteSearch.entity && !userInputs?.agencyId)
      return (
        !inputs.filter(
          (input) =>
            input?.key !== 'agencyId' && input?.required && !input?.value,
        )?.length && autoCompleteSearch?.entity
      );

    return !inputs.filter((input) => input?.required && !input?.value)?.length;
  };

  return (
    <Box>
      {inputs.map((input) => {
        return (
          <Box key={input.key} className={classes.inputContainer}>
            {!['select', 'autoComplete', 'tel', 'checkbox'].includes(
              input?.type,
            ) && (
              <CustomTextField
                type={input.type}
                label={input.label}
                value={input.value}
                required={input.required}
                onChange={(event) => {
                  handleChange(input.key, event.target.value);
                }}
                onBlur={() => {
                  if (input.type === 'email') {
                    setIsEmailValid(
                      validateEmail(
                        (input?.value as string) || 'example@email.com',
                      ),
                    );
                  }
                }}
                error={
                  (isFinishFillInputs && input?.required && !input?.value) ||
                  (input.type === 'email' && !isEmailValid)
                }
                helperText={
                  (isFinishFillInputs &&
                    input?.required &&
                    !input?.value &&
                    input.helperText) ||
                  (input.type === 'email' &&
                    !isEmailValid &&
                    input?.helperText) ||
                  ''
                }
              />
            )}
            {input.type === 'select' && (
              <CustomSelect
                required={input.required}
                label={input.label}
                value={input.value}
                options={input?.options}
                onChange={(e) => {
                  handleChange(input.key, e.target.value as string);
                }}
                errorSelect={
                  isFinishFillInputs && input?.required && !input?.value
                }
                helperText={
                  (isFinishFillInputs &&
                    input?.required &&
                    !input?.value &&
                    input.helperText) ||
                  undefined
                }
              />
            )}
            {input?.type === 'tel' && (
              <CustomPhoneInput
                label="Téléphone"
                required={input?.required}
                value={input?.value}
                onChange={(value: any) => handleChange(input?.key, value)}
                error={
                  (isFinishFillInputs && input?.required && !input?.value) ||
                  (isFinishFillInputs && !isValidPhoneNumber(input?.value))
                }
                helperText={
                  (isFinishFillInputs &&
                    input?.required &&
                    !input?.value &&
                    input.helperText) ||
                  (isFinishFillInputs &&
                    !isValidPhoneNumber(input?.value) &&
                    'Le numéro de téléphone est invalide') ||
                  ''
                }
              />
            )}
            {input?.type === 'autoComplete' && (
              <CustomAutoComplete
                error={
                  isFinishFillInputs &&
                  input.required &&
                  ((input?.key === 'enseignId' &&
                    !autoCompleteSearch?.enseign) ||
                    (input?.key === 'agencyId' &&
                      !autoCompleteSearch?.entity) ||
                    (input?.key === 'mandataryId' &&
                      !autoCompleteSearch?.mandatary))
                }
                helperText={
                  (isFinishFillInputs &&
                    input?.required &&
                    !input?.value &&
                    input.helperText) ||
                  ''
                }
                required={input?.required}
                clearOnBlur={false}
                disableClearable={true}
                loading={
                  input?.key === 'enseignId'
                    ? loadingAutoCompleteEnseigne
                    : input?.key === 'agencyId'
                    ? loadingAutoCompleteEntity
                    : loadingAutoCompleteMandataries
                }
                label={input?.label}
                inputValue={
                  input?.key === 'enseignId'
                    ? autoCompleteSearch?.enseign
                    : input?.key === 'agencyId'
                    ? autoCompleteSearch?.entity
                    : autoCompleteSearch?.mandatary
                }
                onChange={(value) => {
                  hanleAutoCompleteInputChange(
                    input?.key === 'enseignId'
                      ? 'enseign'
                      : input?.key === 'agencyId'
                      ? 'entity'
                      : 'mandatary',
                    value,
                  );
                }}
                onAutoCompleteChange={(option) => {
                  handleChange(input?.key, option?.value);
                }}
                options={input?.options || []}
              />
            )}
            {input?.type === 'checkbox' && (
              <FormControlLabel
                control={
                  <CheckBox
                    color="primary"
                    name={input?.key || ''}
                    onClick={(event: any) =>
                      handleChange(event.target.name, event.target.checked)
                    }
                    checked={!!input?.value}
                  />
                }
                label={input?.label || ''}
              />
            )}
          </Box>
        );
      })}

      <ConfirmationButtons
        containerStyle={{ marginTop: 40 }}
        handleCancel={props.onCancel}
        handleConfirm={() => {
          handleCreate();
          setIsFinishFillInputs(true);
        }}
        disableConfirm={loadingCreateUser}
        confirmText={loadingCreateUser ? 'Chargement...' : undefined}
      />
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    inputContainer: {
      margin: '12px',
    },
  }),
);

export default UserForm;
